import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { FilterCombinationResponse } from '@api-response-types/filter-combination.response';
import { SaveFilterCombinationDto } from '@api-types/dtos/save-filter-combination.dto';
import { UpdateForecastSeriesDto } from '@api-types/dtos/update-forecast-series.dto';
import { IntervalType } from '@api-types/enums/interval-type.enum';
import { Resolution } from '@api-types/enums/resolution.enum';
import { convertFiltersMapToRecord } from '@api-types/helper/helper';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { FilterCombinationHttpClient } from 'demand-forecasting/services/http/filter-combination-http-client';
import { ForecastsHttpClient } from 'demand-forecasting/services/http/forecast-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import {
  selectConfidenceLevel,
  selectIntervalType,
} from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import { ConfigurationRouterHelperService } from 'demand-forecasting/store/router-state/configuration-router-helper.service';
import {
  selectConfigurationId,
  selectConsensusVersionIds,
  selectDemandSourceVersionId,
  selectResolution,
  selectVersionId,
} from 'demand-forecasting/store/router-state/router.selectors';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Observable,
  catchError,
  finalize,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { FilterActions } from './filter.actions';
import {
  selectSavedFilterCombinations,
  selectSelectedFilterCombinationIdsFromUrl,
  selectSelectedFilters,
} from './filter.selectors';

@Injectable()
export class FilterEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly toastService = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly filterCombinationHttpClient = inject(
    FilterCombinationHttpClient,
  );
  private readonly forecastsHttpClient = inject(ForecastsHttpClient);
  private readonly routerHelper = inject(ConfigurationRouterHelperService);
  private readonly dialogRef = inject(MatDialog);
  private readonly router = inject(Router);

  getAvailableFilters$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.getAvailableFilters),
        map((action) => {
          if (!action.forecastVersionId) {
            throw new Error(
              'Cannot get Available Filters without Forecast Version. This error should not happen.',
            );
          }
          this.forecastsHttpClient
            .getAvailableFilters(action.forecastVersionId)
            .pipe(
              tap((filters) => {
                this.store.dispatch(
                  FilterActions.setAvailableFilters({
                    availableFilters: filters,
                  }),
                );
              }),
              catchError((error: HttpErrorResponse) => {
                Sentry.captureException(error, {
                  tags: {
                    feature: 'filter',
                    operation: 'get-available-filters',
                  },
                  extra: {
                    forecastVersionId: action.forecastVersionId,
                    statusCode: error.status,
                    errorMessage: error.message,
                  },
                });
                return EMPTY;
              }),
            )
            .subscribe();
        }),
      ),

    { dispatch: false },
  );

  getAllAvailableFiltersOnRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.getAvailableFilters),
      map(({ forecastVersionId }) =>
        FilterActions.getAllAvailableFilters({ forecastVersionId }),
      ),
    ),
  );

  getAllAvailableFilters$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.getAllAvailableFilters),
        map((action) => {
          if (!action.forecastVersionId) {
            throw new Error(
              'Cannot get all Available Filters without Forecast Version. This error should not happen.',
            );
          }
          this.forecastsHttpClient
            .getAvailableFilters(action.forecastVersionId)
            .pipe(
              tap((filters) => {
                this.store.dispatch(
                  FilterActions.setAllAvailableFilters({
                    allAvailableFilters: filters,
                  }),
                );
              }),
              catchError((error: HttpErrorResponse) => {
                Sentry.captureException(error, {
                  tags: {
                    feature: 'filter',
                    operation: 'get-all-available-filters',
                  },
                  extra: {
                    forecastVersionId: action.forecastVersionId,
                    statusCode: error.status,
                    errorMessage: error.message,
                  },
                });
                this.store.dispatch(
                  FilterActions.setAllAvailableFiltersFailed({ error }),
                );
                return EMPTY;
              }),
            )
            .subscribe();
        }),
      ),
    { dispatch: false },
  );

  getSavedFilterCombinations$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.getSavedFilterCombinations),
        withLatestFrom(this.store.select(selectConfigurationId)),
        map(([, configurationId]) => {
          if (!configurationId) {
            throw new Error(
              'Cannot get Filter Combination without forecast configuration id. This error should not happen',
            );
          }
          this.filterCombinationHttpClient
            .getFilterCombinations(configurationId)
            .pipe(
              tap((filterCombinations) => {
                this.store.dispatch(
                  FilterActions.setSavedFilterCombinations({
                    savedFilterCombinations: filterCombinations,
                  }),
                );
              }),
            )
            .subscribe();
        }),
      ),

    { dispatch: false },
  );

  deleteFilterCombination$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.deleteFilterCombination),
        tap(() =>
          this.store.dispatch(
            FilterActions.setDeleteFilterCombinationIsLoading({
              isLoading: true,
            }),
          ),
        ),
        withLatestFrom(
          this.store.select(selectConfigurationId),
          this.store.select(selectSavedFilterCombinations),
        ),
        switchMap(
          ([
            { filterCombinationId, queryKey },
            forecastConfigurationId,
            currentCombinations,
          ]) => {
            if (!forecastConfigurationId) {
              throw new Error(
                'Cannot delete Filter Combination without forecast version id. This error should not happen',
              );
            }

            return this.filterCombinationHttpClient
              .deleteFilterCombination(
                filterCombinationId,
                forecastConfigurationId,
              )
              .pipe(
                withLatestFrom(
                  this.store.select(
                    selectSelectedFilterCombinationIdsFromUrl(queryKey),
                  ),
                ),
                tap(([, selectedFilterCombinationIds]) => {
                  this.handleDeleteSuccess(
                    filterCombinationId,
                    currentCombinations,
                    selectedFilterCombinationIds,
                  );
                }),
                finalize(() => this.handleDeleteFinalize()),
                catchError((error: HttpErrorResponse) =>
                  this.handleDeleteError(
                    error,
                    filterCombinationId,
                    forecastConfigurationId,
                  ),
                ),
              );
          },
        ),
      ),
    { dispatch: false },
  );

  createAndSaveFilterCombination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.createAndSaveFilterCombination),
      withLatestFrom(
        this.store.select(selectVersionId),
        this.store.select(
          selectSelectedFilterCombinationIdsFromUrl('filterCombinationIds'),
        ),
      ),
      map(([{ name }, resultId, currentSelectedCombinationIds]) => {
        if (!resultId) {
          throw new Error(
            'Cannot create Filter Combination without forecast version id. This error should not happen.',
          );
        }
        return FilterActions.saveFilterCombinationInternal({
          name,
          resultId,
          urlParamKey: 'filterCombinationIds',
          currentSelectedCombinationIds,
          sentryOperation: 'create-and-save-filter-combination',
        });
      }),
    ),
  );

  createAndSaveConsensusFilterCombination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.createAndSaveConsensusFilterCombination),
      withLatestFrom(
        this.store.select(selectConsensusVersionIds),
        this.store.select(
          selectSelectedFilterCombinationIdsFromUrl(
            'consensusFilterCombinationIds',
          ),
        ),
      ),
      map(([{ name }, consensusVersionIds, currentSelectedCombinationIds]) => {
        const resultId = consensusVersionIds[0];
        if (!resultId) {
          throw new Error(
            'Cannot create Consensus Filter Combination without a consensus version id. This error should not happen.',
          );
        }
        return FilterActions.saveFilterCombinationInternal({
          name,
          resultId,
          urlParamKey: 'consensusFilterCombinationIds',
          currentSelectedCombinationIds,
          sentryOperation: 'create-and-save-consensus-filter-combination',
        });
      }),
    ),
  );

  createAndSaveDemandSourceFilterCombination$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterActions.createAndSaveDemandSourceFilterCombination),
      withLatestFrom(
        this.store.select(selectDemandSourceVersionId),
        this.store.select(
          selectSelectedFilterCombinationIdsFromUrl(
            'demandSourceFilterCombinationIds',
          ),
        ),
      ),
      map(([{ name }, versionId, currentSelectedCombinationIds]) => {
        if (!versionId) {
          throw new Error(
            'Cannot create Demand Source Filter Combination without a forecast version id. This error should not happen.',
          );
        }
        return FilterActions.saveFilterCombinationInternal({
          name,
          resultId: versionId,
          urlParamKey: 'demandSourceFilterCombinationIds',
          currentSelectedCombinationIds,
          sentryOperation: 'create-and-save-demand-source-filter-combination',
        });
      }),
    ),
  );

  saveFilterCombinationInternal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.saveFilterCombinationInternal),
        tap(() =>
          this.store.dispatch(
            FilterActions.setCreateAndSaveFilterCombinationIsLoading({
              isLoading: true,
            }),
          ),
        ),
        withLatestFrom(
          this.store.select(selectConfigurationId),
          this.store.select(selectSelectedFilters),
          this.store.select(selectSavedFilterCombinations),
        ),
        map(
          ([
            {
              name,
              resultId,
              urlParamKey,
              currentSelectedCombinationIds,
              sentryOperation,
            },
            forecastConfigurationId,
            selectedFilters,
            currentCombinations,
          ]) => {
            if (!forecastConfigurationId) {
              throw new Error(
                'Cannot create Filter Combination without configuration id. This error should not happen.',
              );
            }
            const filterCombination: SaveFilterCombinationDto = {
              name,
              appliedFilters: convertFiltersMapToRecord(selectedFilters),
            };
            this.filterCombinationHttpClient
              .createFilterCombination(
                filterCombination,
                forecastConfigurationId,
                resultId,
              )
              .pipe(
                tap((newCombination) => {
                  // Add the new combination to the store
                  // so downstream effects have consistent data when the URL updates
                  this.store.dispatch(
                    FilterActions.setSavedFilterCombinations({
                      savedFilterCombinations: [
                        ...currentCombinations,
                        newCombination,
                      ],
                    }),
                  );
                  this.router.navigate([], {
                    queryParams: {
                      [urlParamKey]: [
                        ...currentSelectedCombinationIds,
                        newCombination.id,
                      ].join(','),
                    },
                    queryParamsHandling: 'merge',
                    replaceUrl: true,
                  });
                  // Background refresh to reconcile with server state
                  this.store.dispatch(
                    FilterActions.getSavedFilterCombinations(),
                  );
                }),
                catchError((err: HttpErrorResponse) => {
                  Sentry.captureException(err, {
                    tags: { feature: 'filter', operation: sentryOperation },
                    extra: {
                      forecastConfigurationId,
                      resultId,
                      name,
                      statusCode: err.status,
                      errorMessage: err.message,
                    },
                  });
                  if (err.status === HttpStatusCode.Conflict) {
                    this.toastService.error(
                      this.translate.instant(
                        'FILTER.FILTER_COMBINATION_COULD_NOT_BE_SAVED',
                      ),
                      this.translate.instant(
                        'FILTER.NAME_OR_FILTER_COMBINATION_ALREADY_SAVED',
                      ),
                    );
                  } else {
                    this.toastService.error(
                      this.translate.instant(
                        'FILTER.FILTER_COMBINATION_COULD_NOT_BE_SAVED',
                      ),
                      this.translate.instant('FILTER.PLEASE_TRY_AGAIN_LATER'),
                    );
                  }
                  return EMPTY;
                }),
                finalize(() => {
                  this.toastService.success(
                    undefined,
                    this.translate.instant('FILTER.FILTER_COMBINATION_SAVED'),
                  );
                  this.dialogRef.closeAll();
                }),
              )
              .subscribe();
          },
        ),
        tap(() =>
          this.store.dispatch(
            FilterActions.setCreateAndSaveFilterCombinationIsLoading({
              isLoading: false,
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  getFilterCombinationForecastSeries$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FilterActions.getFilterCombinationForecastSeries),
        withLatestFrom(
          this.store.select(selectForecastVersionByQueryParam),
          this.store.select(selectResolution),
          this.store.select(
            ConfigurationsSelectors.selectFactorColumnByUrlParam,
          ),
          this.store.select(selectIntervalType),
          this.store.select(selectConfidenceLevel),
          this.store.select(selectSavedFilterCombinations),
        ),
        map(
          ([
            { filterCombinationId },
            version,
            resolution,
            factorColumn,
            intervalType,
            confidenceLevel,
            savedFilterCombinations,
          ]) => {
            if (!version) {
              console.error(
                'Could not find version by url param in store - therefore could not load filter combination forecast series',
              );
              return;
            }

            // get filters from saved filter combination
            const filters = savedFilterCombinations.find(
              (filterCombination) =>
                filterCombination.id === filterCombinationId,
            )?.appliedFilters;

            if (!filters) {
              console.error(
                `Could not find filter combination by id ${filterCombinationId} in store - therefore could not load filter combination forecast series`,
              );
              return;
            }

            // create dto based on global state and use filters from saved filter combination
            const updateForecastSeriesDto: UpdateForecastSeriesDto = {
              // note: they are on purpose empty since we do not need them in filter combination forecast series
              additionalTimeSeriesNames: [],
              resolution: resolution as Resolution,
              factorColumn,
              filters,
              intervalType: (intervalType as IntervalType) || null,
              confidenceLevel,
              loadPredictionExplanation: false,
            };

            this.store.dispatch(
              FilterActions.setFilterCombinationForecastSeriesIdIsPending({
                filterCombinationId,
                isPending: true,
              }),
            );

            this.forecastsHttpClient
              .updateForecastSeries(updateForecastSeriesDto, version.forecastId)
              .pipe(
                map((updatedForecastSeries) => {
                  this.store.dispatch(
                    FilterActions.setFilterCombinationForecastSeries({
                      forecastSeries: updatedForecastSeries,
                      savedFilterCombinationId: filterCombinationId,
                    }),
                  );
                }),
                catchError((error: HttpErrorResponse) => {
                  Sentry.captureException(error, {
                    tags: {
                      feature: 'filter',
                      operation: 'get-filter-combination-forecast-series',
                    },
                    extra: {
                      filterCombinationId,
                      versionId: version?.id,
                      forecastId: version?.forecastId,
                      statusCode: error.status,
                      errorMessage: error.message,
                    },
                  });
                  this.toastService.error(
                    this.translate.instant('UTILITY.FORECAST_SERIES_WONT_LOAD'),
                  );
                  return EMPTY;
                }),
                finalize(() => {
                  this.store.dispatch(
                    FilterActions.setFilterCombinationForecastSeriesIdIsPending(
                      {
                        filterCombinationId,
                        isPending: false,
                      },
                    ),
                  );
                }),
              )
              .subscribe();
          },
        ),
      ),
    {
      dispatch: false,
    },
  );

  private handleDeleteSuccess(
    filterCombinationId: string,
    currentCombinations: FilterCombinationResponse[],
    selectedFilterCombinationIds: string[],
  ): void {
    // Remove the deleted combination from the store
    // so downstream effects have consistent data when the URL updates
    this.store.dispatch(
      FilterActions.setSavedFilterCombinations({
        savedFilterCombinations: currentCombinations.filter(
          (c) => c.id !== filterCombinationId,
        ),
      }),
    );

    if (selectedFilterCombinationIds.includes(filterCombinationId)) {
      this.routerHelper.updateSelectedFilterCombinations(
        selectedFilterCombinationIds.filter((id) => id !== filterCombinationId),
      );
    }

    // Background refresh to reconcile with server state
    this.store.dispatch(FilterActions.getSavedFilterCombinations());
  }

  private handleDeleteFinalize(): void {
    this.store.dispatch(
      FilterActions.setDeleteFilterCombinationIsLoading({ isLoading: false }),
    );
    this.toastService.success(
      undefined,
      this.translate.instant('FILTER.FILTER_COMBINATION_DELETED_SUCCESSFULLY'),
    );
  }

  private handleDeleteError(
    error: HttpErrorResponse,
    filterCombinationId: string,
    forecastConfigurationId: string,
  ): Observable<never> {
    Sentry.captureException(error, {
      tags: {
        feature: 'filter',
        operation: 'delete-filter-combination',
      },
      extra: {
        filterCombinationId,
        forecastConfigurationId,
        statusCode: error.status,
        errorMessage: error.message,
      },
    });
    this.toastService.error(
      undefined,
      this.translate.instant('FILTER.FILTER_COMBINATION_DELETED_FAILED'),
    );
    return EMPTY;
  }
}
